import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import { createFilterHandler } from "../../../Utils/FilterUtils";
import httpclient from "../../../Utils";

import { getColumns } from "./columns";
import Filter from "./Filter";
import { useRemoteBay } from "./useRemoteBay";
import ConfirmationDialog from "./ConfirmationDialog";

const INITIAL_FILTER_STATE = {
  bay: "",
};

const fieldMappings = {
  bay: ["bay", "$eq"],
};

export default function RemoteBay() {
  const [selectedBay, setSelectedBay] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [openDeleteBay, setOpenDeleteBay] = useState(false);
  const [isDeletingBay, setIsDeletingBay] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const {
    remoteBays,
    setRemoteBays,
    bayList,
    loading,
    setLoading,
    rowCount,
    refresh,
    refreshRemoteBays,
  } = useRemoteBay({
    autoFetch: true,
    pagination: {
      page: paginationModel.page, // Pass primitive values
      pageSize: paginationModel.pageSize,
    },
    sorting: sortModel,
  });

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setRemoteBays,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/remote-bay`,
    fieldMappings,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] = ""; // Reset other fields to empty string;

    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(INITIAL_FILTER_STATE);
  };

  const handleAddBay = () => {
    navigate("/remote-bay/add");
  };

  const handleDeleteBay = async () => {
    setIsDeletingBay(true);

    const API_URL = `/remote-bay/delete/${selectedBay.id}`;

    try {
      const { data } = await httpclient.post(API_URL);
      if (data.status === 200) {
        showNotification("success", data.message);
        setSelectedBay("");
        setOpenDeleteBay(false);
        refreshRemoteBays(true);
      } else {
        showNotification("info", data.message);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setTimeout(() => {
          setOpenDeleteBay(false); // Close modal
        }, 1000);
      } else if (error.response?.status === 401) {
        refresh();
      }
      handleApiError(error);
    } finally {
      setIsDeletingBay(false);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns(setSelectedBay, setOpenDeleteBay);

  const deleteUserMessage = (
    <>
      Are you sure you want to delete <strong>{selectedBay?.bay}</strong>?
    </>
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Country/Remote Bay
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "4px",
                  marginRight: "16px",
                  backgroundColor: "#7152F3",
                  "&:hover": {
                    backgroundColor: "#5c34fa",
                  },
                }}
                startIcon={<AddIcon sx={{ marginRight: 0 }} />}
                onClick={handleAddBay}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                  }}
                >
                  Add New Bay
                </Typography>
              </Button>
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Box>
          </Box>

          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
            bayList={bayList}
          />

          <DataGrid
            rows={remoteBays || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#48505E",
              },
            }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
          />
        </Box>
      </Box>
      {openDeleteBay && (
        <ConfirmationDialog
          open={openDeleteBay}
          handleClose={() => setOpenDeleteBay(false)}
          title={"Delete Bay"}
          message={deleteUserMessage}
          selectedUser={selectedBay}
          onConfirm={handleDeleteBay}
          loading={isDeletingBay}
        />
      )}
    </>
  );
}
